import {
  allocateShiftsToActivityOccurrence,
  deallocateShiftsFromActivityOccurrence,
} from "@/api/Activities";
import { assignGroupToShift, unassignGroupFromShift } from "@/api/Shifts";
import * as Sentry from "@sentry/react";
import { useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useDragAndDropContext } from "./DragAndDropContext";
import { useDragAndDropMutation } from "./dragAndDropUtils";
import { unassignGroupFromRoute } from "@/api/Routes";
import { useLingui } from "@lingui/react/macro";
import { isGroup } from "@medoma/models/activities";

export const useDropToShift = (shiftId: number) => {
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { drag } = useDragAndDropContext();

  const { mutate: placeActivityOccurrenceInShift, isPending: isAllocating } =
    useDragAndDropMutation({
      mutationFn: ({
        activityId,
        occurrenceId,
      }: {
        activityId: string;
        occurrenceId: string;
      }) =>
        allocateShiftsToActivityOccurrence([shiftId], activityId, occurrenceId),
      errorMessage: t`Det gick inte att lägga till aktiviteten i arbetspasset.`,
    });

  const { mutate: placeGroupInShift, isPending: isPlacingGroupInShift } =
    useDragAndDropMutation({
      mutationFn: ({ groupId }: { groupId: string }) =>
        assignGroupToShift(groupId, shiftId),
      errorMessage: t`Det gick inte att lägga till besöket i arbetspasset.`,
    });

  const {
    mutate: moveActivityOccurrenceToShift,
    isPending: isMovingActivityOccurrenceToShift,
  } = useDragAndDropMutation({
    mutationFn: async ({
      activityId,
      occurrenceId,
      fromShiftId,
    }: {
      activityId: string;
      occurrenceId: string;
      fromShiftId: number;
    }) => {
      await deallocateShiftsFromActivityOccurrence(
        [fromShiftId],
        activityId,
        occurrenceId,
      );
      return allocateShiftsToActivityOccurrence(
        [shiftId],
        activityId,
        occurrenceId,
      );
    },
    errorMessage: t`Det gick inte att lägga till aktiviteten i arbetspasset.`,
  });

  const { mutate: moveGroupToShift, isPending: isMovingGroupToShift } =
    useDragAndDropMutation({
      mutationFn: async ({
        groupId,
        fromShiftId,
      }: {
        groupId: string;
        fromShiftId: number;
      }) => {
        await unassignGroupFromShift(groupId, fromShiftId);
        return assignGroupToShift(groupId, shiftId);
      },
      errorMessage: t`Det gick inte att flytta besöket till arbetspasset.`,
    });

  const { mutate: moveVisitToShift, isPending: isMovingVisitToShift } =
    useDragAndDropMutation({
      mutationFn: async ({
        visitId,
        routeId,
      }: {
        visitId: string;
        routeId: string;
      }) => {
        await unassignGroupFromRoute(visitId, routeId);
        return assignGroupToShift(visitId, shiftId);
      },
      errorMessage: t`Det gick inte att flytta besöket till arbetspasset.`,
    });

  const handleDrop = () => {
    if (!drag) {
      Sentry.captureException(
        new Error("A dropped item is missing the mandatory 'drag'."),
      );
      displayErrorMessageAlert(
        t`Något gick fel. Prova igen. Om felet kvarstår, hör av dig till teknisk support.`,
      );
      return;
    }
    switch (drag.source) {
      case "sidebar":
        return isGroup(drag.item)
          ? placeGroupInShift({ groupId: drag.item.id })
          : placeActivityOccurrenceInShift({
              activityId: drag.item.activityId,
              occurrenceId: drag.item.id,
            });
      case "route":
        return moveVisitToShift({
          visitId: drag.item.id,
          routeId: drag.routeId,
        });
      // A lane's tile stands for that shift's assignment alone: work assigned to
      // several shifts shows up in one lane per shift, so moving one tile moves
      // that shift's assignment and leaves the other assignees where they are,
      // the way a visit dragged off one of its routes stays on the others.
      case "shift-lane":
        return isGroup(drag.item)
          ? moveGroupToShift({
              groupId: drag.item.id,
              fromShiftId: drag.shiftId,
            })
          : moveActivityOccurrenceToShift({
              activityId: drag.item.activityId,
              occurrenceId: drag.item.id,
              fromShiftId: drag.shiftId,
            });
    }
  };

  return {
    handleDrop,
    isPending:
      isAllocating ||
      isPlacingGroupInShift ||
      isMovingActivityOccurrenceToShift ||
      isMovingGroupToShift ||
      isMovingVisitToShift,
  };
};
